import React from 'react';
import {
    Div,
    Group,
    PanelHeaderButton,
    Progress,
    SplitLayout,
    ModalRoot,
    ModalPage,
    ModalPageHeader,
    FormItem, Button, SplitCol, Title, SimpleCell, Avatar
} from '@vkontakte/vkui';
import axios from "axios";
import Camera, {FACING_MODES} from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';

import {
    Icon20Check,
    Icon20RefreshOutline, Icon20RotateRight,
    Icon24BrowserBack,
    Icon24Cancel,
    Icon28CameraOutline, Icon28UploadOutline, Icon16Clear
} from "@vkontakte/icons";
import {AdaptivityContext} from "../assets/constants";
import BaseComponent from "./BaseComponent";

class TakeAPhoto extends BaseComponent {

    constructor(props) {
        super(props)
        const state = {
            progress: 0,
            uploadPending: false,
            uri: null,
            useCamera: null, // New state to track camera or upload choice
            rotationAngle: 0, // Добавляем состояние для угла поворота
            converting: false, // Добавляем состояние для конвертации
            // Новые состояния для множественной загрузки
            selectedFiles: [], // Массив объектов {file, dataUri, id}
            isMultipleMode: false, // Флаг режима множественной загрузки
            currentUploadIndex: 0, // Индекс текущего загружаемого файла
            uploadedCount: 0, // Количество уже загруженных файлов
            selectedAngleId: props.angleId !== undefined && props.angleId !== null ? Number(props.angleId) : null,
            angleStepDone: !(Array.isArray(props.angles) && props.angles.length > 0
                && (props.angleId === undefined || props.angleId === null))
        };

        this.state = {
            ...this.state,
            ...state
        }
        this.fileInputRef = React.createRef()
    }

    // Функция для определения HEIC формата
    isHEICFile = (file) => {
        return file && (
            file.type === 'image/heic' || 
            file.type === 'image/heif' ||
            file.name.toLowerCase().endsWith('.heic') ||
            file.name.toLowerCase().endsWith('.heif')
        );
    }

    // Функция для конвертации HEIC в JPEG
    convertHEICToJPEG = async (file) => {
        try {
            const { default: heic2any } = await import("heic2any");
            const convertedBlob = await heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.9
            });
            
            // heic2any может вернуть массив или один blob
            const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Ошибка конвертации HEIC:', error);
            throw error;
        }
    }

    // Функция для конвертации dataURI в blob
    dataURItoBlob = (dataURI) => {
        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], {type: mimeString});
    }

    // Функция для проверки и конвертации изображения если это HEIC
    processImage = async (dataUri, file = null) => {
        // Если это файл HEIC, конвертируем его
        if (file && this.isHEICFile(file)) {
            try {
                return await this.convertHEICToJPEG(file);
            } catch (error) {
                this.props.openErrorNoty('Ошибка конвертации HEIC изображения!');
                throw error;
            }
        }
        
        // Если это dataUri и возможно HEIC (начинается с data:image/heic или data:image/heif)
        if (dataUri && (dataUri.startsWith('data:image/heic') || dataUri.startsWith('data:image/heif'))) {
            try {
                const blob = this.dataURItoBlob(dataUri);
                return await this.convertHEICToJPEG(blob);
            } catch (error) {
                this.props.openErrorNoty('Ошибка конвертации HEIC изображения!');
                throw error;
            }
        }
        
        return dataUri;
    }

    handleTakePhoto = async (dataUri) => {
        try {
            this.setState({converting: true});
            // Обрабатываем изображение (конвертируем HEIC если нужно)
            const processedDataUri = await this.processImage(dataUri);
            this.setState({uri: processedDataUri, rotationAngle: 0, converting: false}); // Сбрасываем угол поворота для нового фото
            this.setActiveModal("photo_preview");
        } catch (error) {
            console.error('Ошибка обработки фото с камеры:', error);
            this.setState({converting: false});
        }
    }

    handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) {
            this.setState({useCamera: null});
            return;
        }

        try {
            this.setState({converting: true});

            if (files.length === 1) {
                // Одиночный файл - работаем как раньше
                const file = files[0];
                let dataUri;
                
                if (this.isHEICFile(file)) {
                    dataUri = await this.convertHEICToJPEG(file);
                } else {
                    dataUri = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve(e.target.result);
                        reader.readAsDataURL(file);
                    });
                }
                
                this.setState({
                    uri: dataUri, 
                    rotationAngle: 0, 
                    converting: false,
                    isMultipleMode: false
                });
                this.setActiveModal("photo_preview");
            } else {
                // Множественные файлы
                const processedFiles = [];
                
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    let dataUri;
                    
                    if (this.isHEICFile(file)) {
                        dataUri = await this.convertHEICToJPEG(file);
                    } else {
                        dataUri = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (e) => resolve(e.target.result);
                            reader.readAsDataURL(file);
                        });
                    }
                    
                    processedFiles.push({
                        id: Date.now() + i,
                        file: file,
                        dataUri: dataUri,
                        name: file.name
                    });
                }
                
                this.setState({
                    selectedFiles: processedFiles,
                    isMultipleMode: true,
                    converting: false,
                    currentUploadIndex: 0,
                    uploadedCount: 0
                });
                this.setActiveModal("multiple_photos_preview");
            }
        } catch (error) {
            console.error('Ошибка обработки файлов:', error);
            this.setState({useCamera: null, converting: false});
        }
        
        // Очищаем input для возможности повторного выбора тех же файлов
        event.target.value = '';
    };

    // Функция для удаления фото из списка
    removePhoto = (photoId) => {
        this.setState(prevState => ({
            selectedFiles: prevState.selectedFiles.filter(file => file.id !== photoId)
        }));
    };

    // Функция для загрузки всех выбранных фото по очереди
    uploadAllPhotos = async () => {
        const { selectedFiles } = this.state;
        if (selectedFiles.length === 0) return;

        this.setState({ uploadPending: true, progress: 0, currentUploadIndex: 0, uploadedCount: 0 });

        const uploadedImages = []; // Массив для сбора всех загруженных изображений

        try {
            for (let i = 0; i < selectedFiles.length; i++) {
                this.setState({ currentUploadIndex: i });
                
                const fileData = selectedFiles[i];
                const body = new FormData();
                body.append('image', fileData.dataUri);

                await new Promise((resolve, reject) => {
                    axios.post(this.props.url + '/frontend/?app=fileupload2&destination=' + (this.props.destination ?? 'monitoring'), body, {
                        withCredentials: true,
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            // Общий прогресс = (загруженные файлы + текущий прогресс) / общее количество
                            const totalProgress = Math.round(((i * 100) + percentCompleted) / selectedFiles.length);
                            this.setState({ progress: totalProgress });
                        }
                    }).then((response) => {
                        if (response.data.files && response.data.files[0]) {
                            const filename = response.data.files[0].name;
                            
                            // Сохраняем информацию о загруженном изображении
                            uploadedImages.push({
                                image: this.props.url + filename,
                                thumb: this.props.url + '/image.php?w=300&src=' + filename,
                                angle_id: this.getSelectedAngleId()
                            });
                            
                            this.setState(prevState => ({ uploadedCount: prevState.uploadedCount + 1 }));
                            resolve();
                        } else {
                            reject(new Error('Ошибка загрузки файла'));
                        }
                    }).catch(reject);
                });
            }

            // Все файлы загружены успешно - отправляем одно событие со всеми изображениями
            const multipleEventName = this.props.multipleEventName || (this.props.eventName + '_multiple');
            window.postMessage({
                type: multipleEventName,
                data: {
                    numImage: this.props.numImage ?? null,
                    images: uploadedImages // Массив всех загруженных изображений
                }
            });

            this.setActiveModal(null);
            this.props.hidePhotos();
            
            setTimeout(() => {
                this.setState({
                    selectedFiles: [],
                    isMultipleMode: false,
                    uploadPending: false,
                    progress: 0,
                    currentUploadIndex: 0,
                    uploadedCount: 0
                });
            }, 1000);

        } catch (error) {
            console.error('Ошибка загрузки фото:', error);
            this.props.openErrorNoty('Ошибка загрузки одного или нескольких изображений!');
            this.setState({ uploadPending: false });
        }
    };

    // Функция для поворота изображения на 90 градусов
    rotateImage = () => {
        this.setState(prevState => ({
            rotationAngle: (prevState.rotationAngle + 90) % 360
        }));
    };

    // Функция для применения поворота к изображению перед загрузкой
    applyRotationToImage = (dataUri, angle) => {
        return new Promise((resolve) => {
            if (angle === 0) {
                resolve(dataUri);
                return;
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Для углов 90 и 270 градусов меняем местами ширину и высоту
                if (angle === 90 || angle === 270) {
                    canvas.width = img.height;
                    canvas.height = img.width;
                } else {
                    canvas.width = img.width;
                    canvas.height = img.height;
                }

                // Применяем поворот
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate((angle * Math.PI) / 180);
                ctx.drawImage(img, -img.width / 2, -img.height / 2);

                resolve(canvas.toDataURL('image/jpeg', 0.9));
            };

            img.src = dataUri;
        });
    };

    setCurrentPhoto() {
        const self = this
        
        // Применяем поворот к изображению перед загрузкой
        this.applyRotationToImage(this.state.uri, this.state.rotationAngle).then((rotatedDataUri) => {
            const body = new FormData()
            body.append('image', rotatedDataUri)

            this.setState({uploadPending: true, progress: 0})
            axios.post(this.props.url + '/frontend/?app=fileupload2&destination=' + (this.props.destination ?? 'monitoring'), body, {
                withCredentials: true,
                onUploadProgress: function (progressEvent) {
                    const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    self.setState({progress: percentCompleted})
                }
            }).then((response) => {
                if (response.data.files && response.data.files[0]) {
                    const filename = response.data.files[0].name
                    this.setActiveModal(null)
                    this.props.hidePhotos()

                    setTimeout(() => {
                        window.postMessage({
                            type: this.props.eventName ?? "setMonitoringImage",
                            data: {
                                numImage: this.props.numImage ?? null,
                                image: self.props.url + filename,
                                thumb: self.props.url + '/image.php?w=300&src=' + filename,
                                angle_id: self.getSelectedAngleId()
                            }
                        })
                        self.setState({uri: null, progress: 0, uploadPending: false, rotationAngle: 0})
                    }, 1000)
                } else {
                    self.props.openErrorNoty('Ошибка загрузки изображения!')
                }
            });
        });
    }

    chooseCaptureMethod = (method) => {
        if (method === 'camera') {
            this.setState({useCamera: true})
        } else if (method === 'upload') {
            this.setState({useCamera: null}, () => {
                this.fileInputRef.current.click()
            })
        }
    }

    getSelectedAngleId = () => {
        if (this.state.selectedAngleId !== null && this.state.selectedAngleId !== undefined) {
            return Number(this.state.selectedAngleId);
        }
        if (this.props.angleId !== undefined && this.props.angleId !== null) {
            return Number(this.props.angleId);
        }
        return 0;
    }

    selectAngle = (angleId) => {
        this.setState({
            selectedAngleId: Number(angleId),
            angleStepDone: true
        });
    }

    needsAngleSelect = () => {
        const angles = this.props.angles || [];
        return angles.length > 0 && !this.state.angleStepDone;
    }

    renderAngleSelect = () => {
        const angles = this.props.angles || [];
        return (
            <Div>
                <Title level="2" style={{marginBottom: 16, textAlign: 'center'}}>Укажите ракурс</Title>
                {angles.map(angle => (
                    <SimpleCell key={`photo-angle-${angle.id}`} onClick={() => this.selectAngle(angle.id)}>
                        {angle.title}
                    </SimpleCell>
                ))}
            </Div>
        );
    }

    render() {
        const photo_modals = (
            <ModalRoot activeModal={this.state.activeModal}>
                <ModalPage
                    hideCloseButton={true}
                    id="photo_preview"
                    onClose={this.modalBack}
                    settlingHeight={100}
                    style={{width: "100%"}}
                    header={
                        <ModalPageHeader
                            after={<PanelHeaderButton onClick={this.modalBack}><Icon24Cancel/></PanelHeaderButton>}>
                            Сделано фото
                        </ModalPageHeader>
                    }
                >
                    <Group style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        justifyContent: "center",
                        textAlign: "start"
                    }}>
                        {this.state.uploadPending ? <FormItem id="progresslabel" top="Загружаем фото в облако">
                            <Progress aria-labelledby="progresslabel" value={this.state.progress}/>
                        </FormItem> : ''}
                        <img 
                            width="100%" 
                            height={AdaptivityContext.h - 150} 
                            src={this.state.uri}
                            style={{
                                transform: `rotate(${this.state.rotationAngle}deg)`,
                                transition: 'transform 0.3s ease'
                            }}
                        />
                        <Button mode="outline" onClick={() => {
                            this.setCurrentPhoto()
                        }}
                                style={{
                                    color: "lightgreen",
                                    position: 'absolute',
                                    left: '20px',
                                    bottom: '30%'
                                }} size="l"><Icon20Check/></Button>
                        <Button mode="outline" onClick={this.rotateImage} style={{
                            color: "lightblue",
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bottom: '30%'
                        }} size="l"><Icon20RotateRight /></Button>
                        <Button mode="outline" onClick={() => this.modalBack()} style={{
                            color: "orange",
                            position: 'absolute',
                            right: '20px',
                            bottom: '30%'
                        }} size="l"><Icon20RefreshOutline/></Button>
                    </Group>
                </ModalPage>

                <ModalPage
                    hideCloseButton={true}
                    id="multiple_photos_preview"
                    onClose={this.modalBack}
                    settlingHeight={100}
                    style={{width: "100%"}}
                    header={
                        <ModalPageHeader
                            after={<PanelHeaderButton onClick={this.modalBack}><Icon24Cancel/></PanelHeaderButton>}>
                            Выбранные фото ({this.state.selectedFiles.length})
                        </ModalPageHeader>
                    }
                >
                    <Group>
                        {this.state.uploadPending ? (
                            <FormItem id="progresslabel" top={`Загружаем фото ${this.state.uploadedCount + 1} из ${this.state.selectedFiles.length}`}>
                                <Progress aria-labelledby="progresslabel" value={this.state.progress}/>
                            </FormItem>
                        ) : null}
                        
                        <Div style={{ maxHeight: AdaptivityContext.h - 200, overflowY: 'auto' }}>
                            {this.state.selectedFiles.map((fileData, index) => (
                                <SimpleCell
                                    key={fileData.id}
                                    before={
                                        <Avatar size={72} mode="image" src={fileData.dataUri} />
                                    }
                                    after={
                                        !this.state.uploadPending ? (
                                            <Button 
                                                mode="tertiary" 
                                                size="s"
                                                onClick={() => this.removePhoto(fileData.id)}
                                            >
                                                <Icon16Clear />
                                            </Button>
                                        ) : null
                                    }
                                    description={`Файл ${index + 1}`}
                                >
                                    {fileData.name}
                                </SimpleCell>
                            ))}
                        </Div>

                        {!this.state.uploadPending && this.state.selectedFiles.length > 0 ? (
                            <Div style={{ position: 'sticky', bottom: 0, backgroundColor: 'var(--vkui--color_background)', paddingTop: '16px' }}>
                                <Button 
                                    size="l" 
                                    onClick={this.uploadAllPhotos}
                                    style={{ width: '100%' }}
                                >
                                    Загрузить все фото ({this.state.selectedFiles.length})
                                </Button>
                            </Div>
                        ) : null}
                    </Group>
                </ModalPage>
            </ModalRoot>)

        return <SplitLayout modal={photo_modals}>
            <SplitCol>
                <Button mode="secondary" onClick={() => this.props.hidePhotos()}><Icon24BrowserBack/></Button>
                {this.state.converting && (
                    <Div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh'}}>
                        <Progress/>
                        <Title level="3" style={{marginTop: '20px'}}>Конвертируем изображение...</Title>
                    </Div>
                )}
                {this.needsAngleSelect() && !this.state.converting && this.renderAngleSelect()}
                {!this.needsAngleSelect() && this.state.useCamera === null && !this.state.converting && (
                    <Div
                        style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: '80vh'}}>
                        <Button mode="outline" onClick={() => this.chooseCaptureMethod('camera')}
                                style={{padding: '50x', fontSize: '50px'}}>
                            <Icon28CameraOutline width={100} height={100}/>
                            <Title>Камера</Title>
                        </Button>
                        <Button mode="outline" onClick={() => this.chooseCaptureMethod('upload')}>
                            <Icon28UploadOutline width={100} height={100} style={{margin: '50x'}}/>
                            <Title>Файл</Title>
                        </Button>
                    </Div>
                )}
                {!this.needsAngleSelect() && this.state.useCamera && !this.state.converting &&
                    <Camera idealFacingMode={FACING_MODES.ENVIRONMENT}
                            idealRe_solution={[1440, 1920]}
                            is_Fullscreen={true}
                            isMaxResolution={true}
                            sizeFactor={0.5}
                            onTakePhoto={(dataUri) => {
                                this.handleTakePhoto(dataUri);
                            }}
                    />
                }
                    <input
                        type="file"
                        accept="image/*,.heic,.heif"
                        multiple
                        ref={this.fileInputRef}
                        onChange={this.handleFileChange}
                        style={{display: 'none'}}
                    />

            </SplitCol>
        </SplitLayout>
    }
}

export default TakeAPhoto
